'use client';

import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  Combobox as Root,
} from '@/design-system/combobox';
import { useDynamicLocale } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import { useCallback, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useFormField } from '.';
import { FormField } from './Field';
import { UseFormFieldProps } from './type';
interface Option {
  label: string;
  value: string;
  labelEng?: string;
}

interface Props
  extends UseFormFieldProps,
    Omit<React.ComponentProps<typeof Root>, 'items' | 'value' | 'onValueChange' | 'itemToStringLabel'> {
  name: string;
  options: Option[];
  placeholder?: string;
  selected?: string;
}

function FormCombobox(props: Props) {
  const { formFieldProps, childProps, error } = useFormField(props);
  const { control, trigger } = useFormContext();
  const { locale, t } = useDynamicLocale();
  const { options, name, placeholder, selected, disabled } = props;

  const { field } = useController({ name, control });

  const getLabel = (itemValue: unknown) => {
    const option = itemValue as Option | null | undefined;

    return option ? (locale === 'en' && option.labelEng ? option.labelEng : option.label) : '';
  };

  const handleValueChange = (value: unknown) => {
    const option = value as Option | null;

    field.onChange(option?.value ?? '');
    void trigger(name);
  };

  const currentValue = (field.value as string) || selected;
  const selectedOption = options.find((option) => option.value === currentValue) ?? null;
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const setAnchorRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node?.closest<HTMLElement>('[data-slot="dialog-content"]') ?? null);
  }, []);

  return (
    <FormField {...formFieldProps} {...props}>
      <Root
        {...childProps}
        items={options}
        itemToStringLabel={getLabel}
        value={selectedOption}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <ComboboxInput
          ref={setAnchorRef}
          id={name}
          placeholder={placeholder ? placeholder : t('common.form.placeholder.select')}
          showClear
          inputClassName="py-2 placeholder:text-muted-foreground placeholder:font-medium placeholder:text-control disabled:bg-surface-subtle"
          className={cn(error ? 'border-destructive focus-visible:ring-destructive' : '')}
          disabled={disabled}
        />

        <ComboboxContent portalContainer={portalContainer}>
          <ComboboxEmpty>{t('common.form.placeholder.noResults')}</ComboboxEmpty>
          <ComboboxList>
            {(option: Option) => (
              <ComboboxItem key={option.value} value={option}>
                {getLabel(option)}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Root>
    </FormField>
  );
}

export default FormCombobox;
